import React from 'react';
import Radio from 'Components/DesignSystem/Radio/Radio';
import InfoTooltip from 'Components/DesignSystem/Tooltip/InfoTooltip';
import { tooltips } from '../utils/ABHelper';

type TCustomRadioButtonProps = {
    list: string[];
    showOverlayIcon?: boolean;
    selectedItem: string;
    setSelectedItem: (value: string) => void;
    /** Names the group, and templates each option's testid. */
    'data-testid'?: string;
    /** Accessible name for the `radiogroup`. Falls back to the caller's own label. */
    'aria-label'?: string;
};

/**
 * The wizard's radio row — Metrics, Test Design and Promotion Preference all use it
 * (`okf/design-system/e2e.md` § 143).
 *
 * **`role="radiogroup"` is the substantive fix.** These were bare `<input type="radio">`
 * elements sharing no `name`, wrapped in a plain `<div>`. Two consequences, both real:
 * arrow keys did not move between options (a native radio group's roving focus comes
 * from the shared `name`), so a keyboard user had to Tab through all five; and a screen
 * reader announced each as a standalone radio with no group and no "2 of 5" position.
 *
 * `useId` supplies the `name` — the legacy `id` was `` `fruit-${item.toLowerCase()}` ``,
 * a placeholder from whatever this was copied from, and it was **global**: Metrics and
 * Test Design both render on the detail page, so `fruit-daily` and `fruit-revenue`
 * coexisted in one document and any two lists sharing an option label would have
 * collided outright.
 *
 * **The testid was hardcoded** — `components-customradiobutton-input-1` on every option
 * of every list, so up to fifteen elements answered to it on the detail page. It is
 * forwarded now, per the rule in `CLAUDE.md`.
 */
const CustomRadioButton = ({
    list,
    showOverlayIcon = false,
    selectedItem,
    setSelectedItem,
    'data-testid': dataTestId,
    'aria-label': ariaLabel,
}: TCustomRadioButtonProps) => {
    const groupName = React.useId();

    return (
        <div
            role="radiogroup"
            aria-label={ariaLabel}
            className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-6 tw-gap-y-2 tw-mt-1 tw-mb-3"
            data-testid={dataTestId}
        >
            {list?.map((item) => {
                const tooltipMessage = tooltips[item] || '';
                return (
                    <div key={item} className="tw-inline-flex tw-items-center tw-gap-1">
                        <Radio
                            name={groupName}
                            label={item}
                            value={item}
                            checked={selectedItem === item}
                            onChange={(value) => setSelectedItem(String(value))}
                            data-testid={dataTestId ? `${dataTestId}-option-${item}` : undefined}
                        />
                        {/*
                          The tooltip stays a SIBLING of the radio, not part of its label.
                          Inside the `<label>` it would be a click target that selects the
                          option as a side effect of asking what the option means — the
                          same rule `FieldTitle` records for the required marker.
                        */}
                        {showOverlayIcon && tooltipMessage ? (
                            <InfoTooltip
                                message={tooltipMessage}
                                label={`About ${item}`}
                                data-testid={dataTestId ? `${dataTestId}-info-${item}` : undefined}
                            />
                        ) : null}
                    </div>
                );
            })}
        </div>
    );
};

export default CustomRadioButton;
